/**
 * CreateSite — form to create a new site.
 *
 * Collects theme, subdomain, and content source type.
 * - Google Sheets (default): a sheet is created automatically.
 * - JSON URL: the user provides an HTTPS URL serving JSON content.
 * On success displays appropriate links and the API key.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { api, ApiError } from '../api/client.ts';
import { CopyButton } from '../components/CopyButton.tsx';
import { AVAILABLE_THEMES, type ContentSourceType, type CreateSiteResponse } from '../types.ts';

interface FormState {
  themeId: string;
  subdomain: string;
  contentSourceType: ContentSourceType;
  contentSourceId: string;
}

const INITIAL_FORM: FormState = {
  themeId: AVAILABLE_THEMES[0]?.id ?? '',
  subdomain: '',
  contentSourceType: 'sheets',
  contentSourceId: '',
};

/** Validate a subdomain: lowercase alphanumeric + hyphens, 3-48 chars. */
const isValidSubdomain = (value: string): boolean =>
  /^[a-z0-9][a-z0-9-]{1,46}[a-z0-9]$/.test(value);

/** Validate an HTTPS URL. */
const isValidHttpsUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const CreateSite = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [createdResult, setCreatedResult] = useState<CreateSiteResponse | null>(null);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const mutation = useMutation({
    mutationFn: () =>
      api.sites.create({
        themeId: form.themeId,
        subdomain: form.subdomain.trim().toLowerCase(),
        ...(form.contentSourceType === 'json'
          ? {
              contentSourceType: 'json' as const,
              contentSourceId: form.contentSourceId.trim(),
            }
          : {}),
      }),
    onSuccess: (response) => {
      if (response.success && response.data) {
        setCreatedResult(response.data);
        queryClient.invalidateQueries({ queryKey: ['sites'] });
      }
    },
  });

  const validate = (): boolean => {
    const errors: Partial<Record<keyof FormState, string>> = {};

    if (!form.themeId) {
      errors.themeId = t('createSite.validation.themeRequired');
    }

    const sub = form.subdomain.trim().toLowerCase();
    if (!sub) {
      errors.subdomain = t('createSite.validation.subdomainRequired');
    } else if (!isValidSubdomain(sub)) {
      errors.subdomain = t('createSite.validation.subdomainInvalid');
    }

    if (form.contentSourceType === 'json') {
      const url = form.contentSourceId.trim();
      if (!url) {
        errors.contentSourceId = t('createSite.validation.jsonUrlRequired');
      } else if (!isValidHttpsUrl(url)) {
        errors.contentSourceId = t('createSite.validation.jsonUrlInvalid');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validate()) {
      mutation.mutate();
    }
  };

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const isJsonSource = form.contentSourceType === 'json';
  const isCreatedJsonSource = createdResult?.site.contentSourceType === 'json';

  // After successful creation
  if (createdResult) {
    return (
      <div>
        <div className="page-header">
          <h2 className="page-header__title">{t('createSite.success.title')}</h2>
        </div>

        <div className="card">
          <div className="card__body">
            <div className="alert alert--success" role="status">
              <Trans
                i18nKey="createSite.success.message"
                values={{ subdomain: createdResult.site.subdomain }}
                components={{ strong: <strong /> }}
              />
            </div>

            {isCreatedJsonSource ? (
              <div className="mt-6">
                <h3 style={{ marginBottom: 'var(--space-2)' }}>{t('createSite.success.jsonSource.title')}</h3>
                <p style={{ marginBottom: 'var(--space-3)', color: 'var(--color-gray-600)' }}>
                  {t('createSite.success.jsonSource.description')}
                </p>
              </div>
            ) : (
              <div className="mt-6">
                <h3 style={{ marginBottom: 'var(--space-2)' }}>{t('createSite.success.sheetsSource.title')}</h3>
                <p style={{ marginBottom: 'var(--space-3)', color: 'var(--color-gray-600)' }}>
                  {t('createSite.success.sheetsSource.description')}
                </p>
                {createdResult.sheetUrl && (
                  <a
                    href={createdResult.sheetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--primary"
                  >
                    {t('createSite.success.openSheet')}
                  </a>
                )}
              </div>
            )}

            <details className="mt-6" style={{ marginTop: 'var(--space-6)' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                {t('createSite.success.apiKeySection')}
              </summary>
              <div className="api-key-reveal" style={{ marginTop: 'var(--space-3)' }}>
                <p className="api-key-reveal__warning">
                  {t('createSite.success.apiKeyWarning')}
                </p>
                <div className="copy-container">
                  <span className="copy-container__value">{createdResult.apiKey}</span>
                  <CopyButton value={createdResult.apiKey} label={t('createSite.success.copyKey')} />
                </div>
              </div>
            </details>

            <div className="mt-6" style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
              <Link to={`/sites/${createdResult.site.id}`} className="btn btn--secondary">
                {t('createSite.success.viewDashboard')}
              </Link>
              <Link to="/sites" className="btn btn--secondary">
                {t('createSite.success.backToSites')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-header__title">{t('createSite.title')}</h2>
          <p className="page-header__description">
            {t('createSite.description')}
          </p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '600px' }}>
        <form onSubmit={handleSubmit}>
          <div className="card__body">
            {mutation.error && (
              <div className="alert alert--error" role="alert">
                {mutation.error instanceof ApiError && mutation.error.code === 'google_reauth_required' ? (
                  <>
                    {t('createSite.reauthMessage')}{' '}
                    <a href={api.auth.getGoogleLoginUrl()} className="btn btn--primary btn--sm" style={{ marginLeft: 'var(--space-2)' }}>
                      {t('createSite.reauthButton')}
                    </a>
                  </>
                ) : (
                  mutation.error instanceof Error
                    ? mutation.error.message
                    : t('createSite.defaultError')
                )}
              </div>
            )}

            {/* Theme */}
            <div className="form-group">
              <label htmlFor="themeId" className="form-label">
                {t('createSite.theme')}
              </label>
              <select
                id="themeId"
                className="form-select"
                value={form.themeId}
                onChange={(e) => updateField('themeId', e.target.value)}
                aria-invalid={!!validationErrors.themeId}
              >
                {AVAILABLE_THEMES.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.name} — {theme.description}
                  </option>
                ))}
              </select>
              {validationErrors.themeId && (
                <p className="form-error" role="alert">{validationErrors.themeId}</p>
              )}
            </div>

            {/* Subdomain */}
            <div className="form-group">
              <label htmlFor="subdomain" className="form-label">
                {t('createSite.subdomain')}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  id="subdomain"
                  type="text"
                  className={`form-input ${validationErrors.subdomain ? 'form-input--error' : ''}`}
                  value={form.subdomain}
                  onChange={(e) => updateField('subdomain', e.target.value.toLowerCase())}
                  placeholder={t('createSite.subdomainPlaceholder')}
                  aria-describedby="subdomain-helper"
                  aria-invalid={!!validationErrors.subdomain}
                  style={{ flex: 1 }}
                />
                <span style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}>
                  .refineweb.site
                </span>
              </div>
              <p id="subdomain-helper" className="form-helper">
                {t('createSite.subdomainHelper')}
              </p>
              {validationErrors.subdomain && (
                <p className="form-error" role="alert">{validationErrors.subdomain}</p>
              )}
            </div>

            {/* Content Source */}
            <div className="form-group">
              <label htmlFor="contentSourceType" className="form-label">
                {t('createSite.contentSource')}
              </label>
              <select
                id="contentSourceType"
                className="form-select"
                value={form.contentSourceType}
                onChange={(e) => updateField('contentSourceType', e.target.value)}
              >
                <option value="sheets">{t('createSite.contentSourceSheets')}</option>
                <option value="json">{t('createSite.contentSourceJson')}</option>
              </select>
              <p className="form-helper">
                {isJsonSource
                  ? t('createSite.contentSourceJsonHelper')
                  : t('createSite.contentSourceSheetsHelper')}
              </p>
            </div>

            {/* JSON URL (shown only for json source) */}
            {isJsonSource && (
              <div className="form-group">
                <label htmlFor="contentSourceId" className="form-label">
                  {t('createSite.jsonUrl')}
                </label>
                <input
                  id="contentSourceId"
                  type="url"
                  className={`form-input ${validationErrors.contentSourceId ? 'form-input--error' : ''}`}
                  value={form.contentSourceId}
                  onChange={(e) => updateField('contentSourceId', e.target.value)}
                  placeholder={t('createSite.jsonUrlPlaceholder')}
                  aria-invalid={!!validationErrors.contentSourceId}
                />
                {validationErrors.contentSourceId && (
                  <p className="form-error" role="alert">{validationErrors.contentSourceId}</p>
                )}
              </div>
            )}
          </div>

          <div className="card__footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)' }}>
            <Link to="/sites" className="btn btn--secondary">
              {t('common.cancel')}
            </Link>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <span className="spinner" aria-hidden="true" />
                  {t('createSite.creating')}
                </>
              ) : (
                t('createSite.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
